import {
  createContext,
  useState,
  useContext,
  useEffect,
  useCallback,
  useRef,
  useMemo,
  useDeferredValue,
} from 'react';
import { useSelector, useDispatch } from 'react-redux';
import knowledgeServiceApi from 'shared/api/knowledge.service.api';
import { getLang } from 'shared/store/selectors/lang.selector';
import { isAccountAdmin } from 'shared/store/selectors/auth.selector';
import { getUser } from 'shared/store/selectors/user.selector';
import { getCategoriesWithItems } from 'shared/store/selectors/knowledge.selector';
import { triggerKnowledgeUpdate } from 'shared/store/actions/knowledge.actions';
import { useServerSideSearchParams } from 'src/components/design-system/table/hooks';
import { DEFAULT_SORT_DEFINITIONS } from './constants';
import { getResponseErrorMessage, mapColumnKeysToApiParams } from './my-content.utils';
import { EXCLUDED_SYNC_PROVIDERS } from './constants';

const MyContentContext = createContext();

export function useMyContentContext() {
  return useContext(MyContentContext);
}

export function MyContentProvider({ children }) {
  const lang = useSelector(getLang('MY_CONTENT'));
  const user = useSelector(getUser);
  const userIsAccountAdmin = useSelector(isAccountAdmin);
  const categoriesWithItems = useSelector(getCategoriesWithItems);
  const dispatch = useDispatch();

  const [contentItems, setContentItems] = useState([]);
  const [pageLoading, setPageLoading] = useState(true);
  const [searchLoading, setSearchLoading] = useState(false);
  const [actionLoading, setActionLoading] = useState(false);
  const [error, setError] = useState('');

  const [totalItems, setTotalItems] = useState(0);
  const [expiredCount, setExpiredCount] = useState(0);
  const [totalViews, setTotalViews] = useState(0);

  // Ref to ServerSideTable for imperative actions
  const serverSideTableRef = useRef(null);

  // Selection state
  const [selectedRowKeys, setSelectedRowKeys] = useState([]);

  // Clear both React state and AG Grid selection
  const clearSelection = useCallback(() => {
    setSelectedRowKeys([]);
    serverSideTableRef.current?.clearSelection();
  }, []);

  // Handle selection changes from ServerSideTable
  const handleSelectionChange = useCallback(selectedRows => {
    setSelectedRowKeys(selectedRows.map(row => row.id));
  }, []);

  // Memoize defaultFilters to prevent creating new arrays on every render
  const defaultFilters = useMemo(
    () => ({
      searchText: '',
      owners: userIsAccountAdmin ? [] : [user?.id || ''],
      specialty: [],
      path: [],
      shared: null, // null means no filter selected (will be parsed as boolean when set)
      expiresAt: '',
    }),
    [userIsAccountAdmin, user?.id],
  );

  // URL state management
  const { filters, sortBy, sortOrder, params, updateParams } = useServerSideSearchParams({
    defaultFilters,
    defaultSortBy: DEFAULT_SORT_DEFINITIONS.sortBy,
    defaultSortOrder: DEFAULT_SORT_DEFINITIONS.sortOrder,
    defaultPageSize: DEFAULT_SORT_DEFINITIONS.pageSize,
    onUpdateParams: clearSelection,
  });

  // Initialize URL with initial sort on first load only
  const hasInitializedUrlRef = useRef(false);
  useEffect(() => {
    if (!hasInitializedUrlRef.current) {
      hasInitializedUrlRef.current = true;
      // If URL doesn't have sort params, set initial sort
      const urlParams = new URLSearchParams(window.location.search);
      if (!urlParams.has('sortBy')) {
        updateParams(
          {
            sortBy: DEFAULT_SORT_DEFINITIONS.initialSortBy,
            sortOrder: DEFAULT_SORT_DEFINITIONS.initialSortOrder,
          },
          false,
        );
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Deferred values for search - keeps UI responsive while results load
  const deferredSearchText = useDeferredValue(filters.searchText);
  const isSearchStale = deferredSearchText !== filters.searchText;

  // Ref for stable contentItems lookups in callbacks
  const contentItemsRef = useRef(contentItems);
  useEffect(() => {
    contentItemsRef.current = contentItems;
  }, [contentItems]);

  // Data fetching function for ServerSideTable
  const fetchData = useCallback(async apiParams => {
    return knowledgeServiceApi.getItemsByPageForMyContent({
      ...mapColumnKeysToApiParams(apiParams),
      excludeExternalSyncProviders: EXCLUDED_SYNC_PROVIDERS,
    });
  }, []);

  // Handle data loaded from ServerSideTable
  const handleDataLoad = useCallback((items, total, metadata) => {
    setTotalItems(total);
    setExpiredCount(metadata.expiredCount || 0);
    setTotalViews(metadata.totalViews || 0);
    setPageLoading(false);
    setSearchLoading(false);

    // Track loaded items for use outside AG Grid
    setContentItems(prev => {
      const map = new Map(prev.map(item => [item.id, item]));
      items.forEach(item => map.set(item.id, item));
      return Array.from(map.values());
    });
  }, []);

  // Handle data error from ServerSideTable
  const handleDataError = useCallback(err => {
    setError(err);
    setPageLoading(false);
    setSearchLoading(false);
  }, []);

  // Helper to purge cache (used after mutations)
  const purgeCache = useCallback(() => {
    setContentItems([]);
    serverSideTableRef.current?.purgeCache();
  }, []);

  // Handle filter updates from ActiveFilters component
  const handleFilterChange = useCallback(
    (key, newValue) => {
      updateParams({ [key]: newValue });
    },
    [updateParams],
  );

  // Reset all column filters but preserve searchText
  const resetColumnFilters = useCallback(() => {
    const { searchText, ...filterDefaults } = defaultFilters;
    updateParams(filterDefaults, true);
  }, [defaultFilters, updateParams]);

  // Set searchLoading when filter/sort params change
  const prevFilterSortKeyRef = useRef(null);
  useEffect(() => {
    const { page, pageSize, ...filterSort } = params;
    const filterSortKey = JSON.stringify(filterSort);

    if (prevFilterSortKeyRef.current !== null && prevFilterSortKeyRef.current !== filterSortKey) {
      setSearchLoading(true);
    }
    prevFilterSortKeyRef.current = filterSortKey;
  }, [params]);

  /**
   * Triggers content update by dispatching Redux action.
   * This can be called from other components to refresh my-content data.
   */
  const triggerContentUpdate = useCallback(
    (options = {}) => {
      dispatch(triggerKnowledgeUpdate(options));
      purgeCache();
    },
    [dispatch, purgeCache],
  );

  const removeContentItem = useCallback(
    id => {
      setTotalItems(prev => prev - 1);
      dispatch(triggerKnowledgeUpdate());
      purgeCache();
    },
    [dispatch, purgeCache],
  );

  const extendItemExpiration = useCallback(
    async (id, newExpiresAt, refreshKnowledge = true) => {
      setActionLoading(true);

      const response = await knowledgeServiceApi.updateKnowledgeItem({
        id,
        data: { expiresAt: newExpiresAt },
      });

      if (response.error) {
        const errorMessage = getResponseErrorMessage(response);
        setError(errorMessage);
        setActionLoading(false);
        return { error: errorMessage };
      }

      if (refreshKnowledge) {
        dispatch(triggerKnowledgeUpdate());
        purgeCache();
      }

      setActionLoading(false);
      return { success: true };
    },
    [dispatch, purgeCache],
  );

  const shareItemToHub = useCallback(
    async (id, shareToHub = true, refreshKnowledge = true) => {
      setActionLoading(true);

      const response = await knowledgeServiceApi.updateKnowledgeItem({
        id,
        data: { shareToHub },
      });

      if (response.error) {
        const errorMessage = getResponseErrorMessage(response);
        setError(errorMessage);
        setActionLoading(false);
        return { error: errorMessage };
      }

      if (refreshKnowledge) {
        dispatch(triggerKnowledgeUpdate());
        purgeCache();
      }

      setActionLoading(false);
      return { success: true };
    },
    [dispatch, purgeCache],
  );

  const moveItemToCategory = useCallback(
    async (id, categoryId, refreshKnowledge = true) => {
      const item = contentItemsRef.current.find(i => i.id === id);
      const sourceId = item?.links?.find(link => !link.linked)?.categoryId;

      const response = await knowledgeServiceApi.moveKnowledgeItem({
        id,
        categoryId,
        sourceId,
      });

      if (response.error) {
        return { error: getResponseErrorMessage(response) };
      }

      if (refreshKnowledge) {
        dispatch(triggerKnowledgeUpdate());
        purgeCache();
      }

      return { success: true, id, categoryId };
    },
    [dispatch, purgeCache],
  );

  const updateItemOwnership = useCallback(
    async (id, ownerIds, refreshKnowledge = true) => {
      const response = await knowledgeServiceApi.updateKnowledgeItem({
        id,
        data: { ownerIds },
      });

      if (response.error) {
        return { error: getResponseErrorMessage(response) };
      }

      if (refreshKnowledge) {
        dispatch(triggerKnowledgeUpdate());
        purgeCache();
      }

      return { success: true, id, ownerIds };
    },
    [dispatch, purgeCache],
  );

  const value = {
    lang,
    contentItems,
    totalItems,
    expiredCount,
    totalViews,
    categoriesWithItems,
    pageLoading,
    searchLoading,
    actionLoading,
    isSearchStale,
    error,
    filters,
    sortBy,
    sortOrder,
    params,
    updateFilter: updateParams,
    resetFilters: resetColumnFilters,
    handleFilterChange,
    serverSideTableRef,
    fetchData,
    handleDataLoad,
    handleDataError,
    removeContentItem,
    extendItemExpiration,
    shareItemToHub,
    moveItemToCategory,
    updateItemOwnership,
    triggerContentUpdate,
    selectedRowKeys,
    clearSelection,
    handleSelectionChange,
  };

  return <MyContentContext.Provider value={value}>{children}</MyContentContext.Provider>;
}
